/**
 * Stream ECharts theme configuration.
 *
 * Registers `stream-dark` and `stream-light` themes that map ECharts visual
 * properties to Stream design tokens. Import this module after `register.ts`
 * to make themes available for chart components.
 *
 * Per `STREAM_VISUALIZATION.md §5`, ECharts themes require resolved color
 * values (not CSS variables), so we use `tokens.*` from the JS token map.
 */

import { echarts } from './register'
import * as tokens from '@/lib/tokens'
import { SERIES_PALETTE } from './palette'

// ── Dark Theme ──────────────────────────────────────────────────────────────

export const STREAM_DARK_THEME = {
  color: SERIES_PALETTE,
  backgroundColor: 'transparent',

  // Typography
  textStyle: {
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    color: tokens.textSecondary,
    fontSize: 12,
  },

  // Title
  title: {
    textStyle: {
      color: tokens.textPrimary,
      fontSize: 14,
      fontWeight: 600,
    },
    subtextStyle: {
      color: tokens.textMuted,
      fontSize: 12,
    },
  },

  // Legend
  //
  // Active items use `textPrimary` (clearly bright) and inactive items use
  // `textFaint` (clearly dim). ECharts' default `inactiveColor` is `#ccc`,
  // which is BRIGHTER than the dark-theme body text — that inverts the
  // intuitive "bright = visible, dim = hidden" mapping and makes toggled-off
  // series look more prominent than toggled-on ones. We override here so
  // the visual hierarchy reads correctly in both themes.
  legend: {
    textStyle: {
      color: tokens.textPrimary,
      fontSize: 12,
    },
    inactiveColor: tokens.textFaint,
    inactiveBorderColor: 'transparent',
    pageTextStyle: {
      color: tokens.textMuted,
    },
  },

  // Tooltip
  tooltip: {
    backgroundColor: tokens.bgElevated,
    borderColor: tokens.rgba(tokens.textPrimary, 0.1),
    borderWidth: 1,
    textStyle: {
      color: tokens.textPrimary,
      fontSize: 12,
    },
    extraCssText: `
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      border-radius: 6px;
    `,
  },

  // Axis
  categoryAxis: {
    axisLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.1),
      },
    },
    axisTick: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
      },
    },
    axisLabel: {
      color: tokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
        type: 'dashed',
      },
    },
  },

  valueAxis: {
    axisLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.1),
      },
    },
    axisTick: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
      },
    },
    axisLabel: {
      color: tokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
        type: 'dashed',
      },
    },
  },

  timeAxis: {
    axisLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.1),
      },
    },
    axisTick: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
      },
    },
    axisLabel: {
      color: tokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.06),
        type: 'dashed',
      },
    },
  },

  // Data Zoom
  dataZoom: {
    backgroundColor: tokens.rgba(tokens.bgSecondary, 0.8),
    dataBackgroundColor: tokens.rgba(tokens.textMuted, 0.2),
    fillerColor: tokens.rgba(tokens.accentPrimary, 0.1),
    handleColor: tokens.accentPrimary,
    handleSize: '80%',
    textStyle: {
      color: tokens.textMuted,
    },
    borderColor: tokens.rgba(tokens.textPrimary, 0.06),
  },

  // Visual Map (for heatmaps)
  visualMap: {
    textStyle: {
      color: tokens.textMuted,
    },
    inRange: {
      color: [
        tokens.rgba(tokens.accentPrimary, 0.1),
        tokens.rgba(tokens.accentPrimary, 0.3),
        tokens.rgba(tokens.accentPrimary, 0.5),
        tokens.rgba(tokens.accentPrimary, 0.7),
        tokens.accentPrimary,
      ],
    },
  },

  // Calendar (for calendar heatmaps)
  calendar: {
    itemStyle: {
      color: tokens.bgSecondary,
      borderColor: tokens.rgba(tokens.textPrimary, 0.06),
      borderWidth: 1,
    },
    dayLabel: {
      color: tokens.textMuted,
      fontSize: 10,
    },
    monthLabel: {
      color: tokens.textSecondary,
      fontSize: 11,
    },
    yearLabel: {
      color: tokens.textSecondary,
      fontSize: 12,
    },
    splitLine: {
      lineStyle: {
        color: tokens.rgba(tokens.textPrimary, 0.1),
      },
    },
  },

  // Line series defaults
  line: {
    smooth: false,
    symbol: 'circle',
    symbolSize: 4,
    lineStyle: {
      width: 2,
    },
  },

  // Bar series defaults
  bar: {
    barMaxWidth: 50,
    itemStyle: {
      borderRadius: [4, 4, 0, 0],
    },
  },

  // Heatmap series defaults
  heatmap: {
    itemStyle: {
      borderColor: tokens.bgPrimary,
      borderWidth: 1,
    },
  },

  // Animation defaults
  animationDuration: 400,
  animationEasing: 'cubicOut',
}

// ── Light Theme ─────────────────────────────────────────────────────────────

// Light mode token overrides
const lightTokens = {
  bgPrimary: '#f8fafc',
  bgSecondary: '#ffffff',
  bgTertiary: '#f1f5f9',
  bgElevated: '#ffffff',
  textPrimary: '#0f172a',
  textSecondary: '#334155',
  textMuted: '#64748b',
  textFaint: '#94a3b8',
}

export const STREAM_LIGHT_THEME = {
  ...STREAM_DARK_THEME,

  textStyle: {
    ...STREAM_DARK_THEME.textStyle,
    color: lightTokens.textSecondary,
  },

  title: {
    textStyle: {
      ...STREAM_DARK_THEME.title.textStyle,
      color: lightTokens.textPrimary,
    },
    subtextStyle: {
      color: lightTokens.textMuted,
      fontSize: 12,
    },
  },

  legend: {
    textStyle: {
      color: lightTokens.textPrimary,
      fontSize: 12,
    },
    inactiveColor: lightTokens.textFaint,
    inactiveBorderColor: 'transparent',
    pageTextStyle: {
      color: lightTokens.textMuted,
    },
  },

  tooltip: {
    ...STREAM_DARK_THEME.tooltip,
    backgroundColor: lightTokens.bgElevated,
    borderColor: 'rgba(0, 0, 0, 0.08)',
    textStyle: {
      color: lightTokens.textPrimary,
      fontSize: 12,
    },
    extraCssText: `
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
      border-radius: 6px;
    `,
  },

  categoryAxis: {
    ...STREAM_DARK_THEME.categoryAxis,
    axisLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.08)' },
    },
    axisTick: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)' },
    },
    axisLabel: {
      color: lightTokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)', type: 'dashed' },
    },
  },

  valueAxis: {
    ...STREAM_DARK_THEME.valueAxis,
    axisLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.08)' },
    },
    axisTick: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)' },
    },
    axisLabel: {
      color: lightTokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)', type: 'dashed' },
    },
  },

  timeAxis: {
    ...STREAM_DARK_THEME.timeAxis,
    axisLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.08)' },
    },
    axisTick: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)' },
    },
    axisLabel: {
      color: lightTokens.textMuted,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.04)', type: 'dashed' },
    },
  },

  dataZoom: {
    ...STREAM_DARK_THEME.dataZoom,
    backgroundColor: 'rgba(241, 245, 249, 0.8)',
    dataBackgroundColor: 'rgba(100, 116, 139, 0.2)',
    borderColor: 'rgba(0, 0, 0, 0.04)',
  },

  calendar: {
    ...STREAM_DARK_THEME.calendar,
    itemStyle: {
      color: lightTokens.bgSecondary,
      borderColor: 'rgba(0, 0, 0, 0.04)',
      borderWidth: 1,
    },
    dayLabel: {
      color: lightTokens.textMuted,
      fontSize: 10,
    },
    monthLabel: {
      color: lightTokens.textSecondary,
      fontSize: 11,
    },
    yearLabel: {
      color: lightTokens.textSecondary,
      fontSize: 12,
    },
    splitLine: {
      lineStyle: { color: 'rgba(0, 0, 0, 0.08)' },
    },
  },

  heatmap: {
    itemStyle: {
      borderColor: lightTokens.bgPrimary,
      borderWidth: 1,
    },
  },
}

// ── Register Themes ─────────────────────────────────────────────────────────

echarts.registerTheme('stream-dark', STREAM_DARK_THEME)
echarts.registerTheme('stream-light', STREAM_LIGHT_THEME)

// Visible bootstrap signal: surfaces in the browser console if (and only if)
// this module was actually imported at app start. Useful when investigating
// "the theme tokens look wrong / nothing changed when we updated the theme"
// — if you don't see this line, the theme module never ran and ECharts is
// using its defaults.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.debug('[charts] registered ECharts themes: stream-dark, stream-light')
}

export { echarts }
